export interface BuildContext {
	cwd: string;
	outDir: string;
	mode: 'development' | 'production';
	target: DeploymentTarget;
}

export interface DeploymentTarget {
	name: string;
	platform: 'static' | 'server' | 'serverless' | 'cdn';
	config: Record<string, any>;
}

export interface AdapterOptions {
	outDir?: string;
	publicPath?: string;
	prerender?: boolean;
	generateSitemap?: boolean;
	compression?: boolean;
}

export abstract class BaseAdapter {
	protected name: string;
	protected platform: DeploymentTarget['platform'];
	protected options: AdapterOptions;

	constructor(name: string, platform: DeploymentTarget['platform'], options: AdapterOptions = {}) {
		this.name = name;
		this.platform = platform;
		this.options = {
			outDir: 'dist',
			publicPath: '/',
			prerender: true,
			generateSitemap: false,
			compression: true,
			...options,
		};
	}

	/**
	 * Prepare the build configuration for this adapter
	 */
	abstract prepareBuild(context: BuildContext): Promise<any>;

	/**
	 * Process the build output after compilation
	 */
	abstract processBuild(context: BuildContext): Promise<void>;

	/**
	 * Generate deployment-specific files
	 */
	abstract generateDeploymentFiles(context: BuildContext): Promise<void>;

	/**
	 * Validate deployment requirements
	 */
	abstract validateDeployment(context: BuildContext): Promise<boolean>;

	/**
	 * Get the adapter's display name
	 */
	getName(): string {
		return this.name;
	}

	/**
	 * Get the target platform
	 */
	getPlatform(): DeploymentTarget['platform'] {
		return this.platform;
	}

	/**
	 * Get adapter options
	 */
	getOptions(): AdapterOptions {
		return this.options;
	}

	/**
	 * Log adapter-specific messages
	 */
	protected log(message: string): void {
		console.log(`[Frame ${this.name}] ${message}`);
	}

	/**
	 * Log errors with adapter context
	 */
	protected logError(message: string, error?: Error): void {
		console.error(`[Frame ${this.name}] ${message}`, error || '');
	}
} 